import React from 'react';
import styled from 'styled-components';
import { TranscriptVersion } from '../../../repositories/TranscriptRepository';
import CloseIcon from '@src/svgs/CloseIcon';

interface TranscriptVersionSwitchModalProps {
  isOpen: boolean;
  targetVersion: TranscriptVersion | null;
  onConfirm: () => void;
  onCancel: () => void;
}

const versionLabels: Record<TranscriptVersion, string> = {
  [TranscriptVersion.LATEST]: 'Latest',
  [TranscriptVersion.ORIGINAL]: 'Original',
  [TranscriptVersion.REV]: 'Rev',
  [TranscriptVersion.AWS]: 'AWS',
  [TranscriptVersion.ASSEMBLY]: 'Assembly',
  [TranscriptVersion.AWS_REV]: 'AWS + Rev',
};

export const TranscriptVersionSwitchModal: React.FC<TranscriptVersionSwitchModalProps> = ({
  isOpen,
  targetVersion,
  onConfirm,
  onCancel,
}) => {
  if (!isOpen || !targetVersion) {
    return null;
  }

  return (
    <ModalOverlay>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Switch Transcript Version</ModalTitle>
          <CloseButton onClick={onCancel}>
            <CloseIcon width="16" height="16" />
          </CloseButton>
        </ModalHeader>
        
        <ModalBody>
          <p>
            Switching to <strong>{versionLabels[targetVersion]}</strong> version of the transcript.
          </p>
          <WarningText>
            <strong>Warning:</strong> If you make changes to this version and save, only the Latest version will be updated.
            To keep your changes, make sure to switch back to Latest before saving.
          </WarningText>
        </ModalBody>
        
        <ModalFooter>
          <CancelButton onClick={onCancel}>Cancel</CancelButton>
          <ConfirmButton onClick={onConfirm}>Switch Version</ConfirmButton>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: #030419;
  color: #f3e9d7;
  width: 450px;
  border-radius: 6px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #1e1e2c;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  color: #f2d093;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #f3e9d7;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: #ffffff;
  }
`;

const ModalBody = styled.div`
  padding: 20px;
  
  p {
    margin: 0 0 16px;
    line-height: 1.5;
    font-size: 14px;
  }
  
  p:last-child {
    margin-bottom: 0;
  }
`;

const WarningText = styled.p`
  padding: 12px;
  background-color: rgba(242, 208, 147, 0.1);
  border-left: 3px solid #f2d093;
  
  strong {
    color: #f2d093;
    font-weight: 600;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px 20px;
  border-top: 1px solid #1e1e2c;
  gap: 12px;
`;

const Button = styled.button`
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
`;

const CancelButton = styled(Button)`
  background-color: transparent;
  color: #a9a9a9;
  border: 1px solid #484848;
  
  &:hover {
    background-color: rgba(242, 208, 147, 0.1);
    border-color: #a9a9a9;
  }
`;

const ConfirmButton = styled(Button)`
  background-color: #f2d093;
  color: #030419;
  border: 1px solid #f2d093;
  
  &:hover {
    background-color: #d9b77e;
    border-color: #d9b77e;
  }
`;

export default TranscriptVersionSwitchModal;